import { useEffect, useState } from "react";
import { Loader2, MapPin, Tractor, X } from "lucide-react";
import { api } from "../../api";
import { reportSwallowed } from "../../lib/reportSwallowed";
import { ApiError } from "../../api/client";
import { useJobs } from "../../hooks/useJobTracker";
import { useJdConnect } from "../../hooks/useJdConnect";
import { JdReconsentNote } from "../../components/JdReconsentNote";
import type { DashboardField } from "../../types/contract";

// Shared John-Deere import step: the connect → link → import states, in one place.
function errMsg(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  return e instanceof Error ? e.message : "Something went wrong.";
}

export function JdImportStep({
  field,
  title,
  intro,
  onLink,
  onDone,
  onSkip,
  onClose,
}: {
  field: DashboardField;
  title: string;
  intro: string;
  onLink: () => void;
  onDone: () => void;
  onSkip: () => void;
  onClose: () => void;
}) {
  const { status, refresh, connect, connecting } = useJdConnect();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // useJdConnect doesn't fetch status on mount — kick it so we show the right state (sign-in vs
  // link vs import) instead of defaulting to "not connected".
  useEffect(() => {
    void refresh().catch((err) =>
      reportSwallowed("refresh John Deere status", err),
    );
  }, [refresh]);

  async function pull(mode: "all" | "yield") {
    setBusy(true);
    setErr(null);
    try {
      const job = await api.jdPullOperations(field.field_id, { mode });
      void api
        .buildCovariates(field.field_id)
        .catch((err) => reportSwallowed("build covariates", err));
      // ONE JOB PER JD FIELD — a merged field with two links files two pulls, and following only
      // the first would report the import done while half was in flight.
      const jobIds = job.job_ids?.length
        ? job.job_ids
        : job?.job_id
          ? [job.job_id]
          : [];
      const what =
        mode === "yield" ? "Importing JD yield" : "Importing JD machine data";
      jobIds.forEach((id, i) =>
        useJobs.getState().upsert({
          job_id: id,
          pct: 0,
          status: "running",
          message: `${what}${jobIds.length > 1 ? ` — JD field ${i + 1} of ${jobIds.length}` : ""}…`,
        }),
      );
      onDone();
    } catch (e) {
      setErr(errMsg(e));
      setBusy(false);
    }
  }

  const connected = status?.connected;
  const linked = field.has_jd_link;

  return (
    <>
      <div className="flex items-center justify-between border-b border-line px-3 py-2">
        <h2 className="flex items-center gap-1.5 text-sm font-semibold">
          <Tractor size={15} className="text-muted" /> {title}
        </h2>
        <button
          onClick={onClose}
          className="rounded p-1 hover:bg-hover"
          aria-label="Close"
        >
          <X size={16} />
        </button>
      </div>
      <div className="flex flex-col gap-3 p-4 text-[12px]">
        <p className="text-muted">{intro}</p>
        <JdReconsentNote />
        {status === null ? (
          <span className="flex items-center gap-1.5 text-muted">
            <Loader2 size={12} className="animate-spin" /> Checking your John
            Deere connection…
          </span>
        ) : !status.configured ? (
          <span className="text-muted">
            John Deere Ops isn’t available in this environment yet — you can add
            machine data later once it’s switched on.
          </span>
        ) : !connected ? (
          <button
            className="flex items-center gap-1.5 self-start rounded bg-accent px-3 py-1.5 text-canvas hover:bg-accent-hover disabled:opacity-50"
            disabled={connecting}
            onClick={connect}
          >
            {connecting ? (
              <Loader2 size={12} className="animate-spin" />
            ) : (
              <Tractor size={13} />
            )}{" "}
            Sign in to JD Ops
          </button>
        ) : !linked ? (
          <button
            className="flex items-center gap-1.5 self-start rounded bg-accent px-3 py-1.5 text-canvas hover:bg-accent-hover"
            onClick={onLink}
          >
            <MapPin size={13} /> Link to a JD Ops field
          </button>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            <button
              className="rounded bg-accent px-3 py-1.5 text-canvas hover:bg-accent-hover disabled:opacity-50"
              disabled={busy}
              onClick={() => void pull("all")}
            >
              {busy ? (
                <Loader2 size={12} className="inline animate-spin" />
              ) : null}{" "}
              Import all data
            </button>
            <button
              className="rounded border border-line px-3 py-1.5 text-fg hover:bg-hover disabled:opacity-50"
              disabled={busy}
              onClick={() => void pull("yield")}
            >
              Import just yield
            </button>
          </div>
        )}
        {err && <span className="text-red-400">{err}</span>}
        {/* Skip is disabled during an in-flight import so it can't race the pull's onDone. */}
        <button
          className="self-start text-[11px] text-muted underline hover:text-fg disabled:opacity-50"
          disabled={busy}
          onClick={onSkip}
        >
          Skip — continue without JD data
        </button>
      </div>
    </>
  );
}
